/**
 * Scroll-trigger collection cards.
 *
 * Loaded by sections/scrolltrigger-collection-cards.liquid with
 *   <script type="module" src="{{ 'scrolltrigger-collection-cards.js' | asset_url }}"></script>
 * ES modules are deduplicated by URL, so several instances of the section on one page still
 * fetch and execute this exactly once.
 *
 * Never append custom code to assets/theme.js — a Prestige update overwrites it.
 *
 * ---------------------------------------------------------------------------------------------
 * How the effect is split between CSS and JS
 * ---------------------------------------------------------------------------------------------
 * The PIN is pure CSS: .stcc__pin is `position: sticky`, which is exactly "scroll normally until
 * you reach the top of the screen, then stay put". No JS pins anything, so there is no injected
 * spacer and nothing to refresh when the theme editor re-renders the section. (This is why the
 * section needs no GSAP/ScrollTrigger — its headline feature, pin: true, is what sticky already
 * does natively.)
 *
 * JS only answers two questions per scroll frame:
 *   1. how far through the pinned stretch are we, 0 → 1?
 *   2. therefore how far down should each card sit?
 * and writes the answer to one custom property per card (--stcc-shift). The transform itself is
 * declared in CSS.
 *
 * ---------------------------------------------------------------------------------------------
 * Smoothing (the "free motion" glide)
 * ---------------------------------------------------------------------------------------------
 * The cards are not welded to the scrollbar. Scrolling moves a TARGET progress; what actually gets
 * painted is a SMOOTHED progress that eases toward it, so the cards keep gliding for a moment after
 * the user stops. This is the same idea as GSAP's `scrub: <seconds>` — and note it smooths only this
 * section's animation, never the page scroll itself, so it costs nothing elsewhere on the site
 * (page-wide smooth scrolling is a much larger change that fights the theme's sticky header,
 * carousel snapping and drawer overscroll).
 *
 * The easing is frame-rate independent: `alpha = 1 - exp(-dt / tau)` rather than a fixed per-frame
 * fraction, which would glide roughly twice as fast on a 120Hz display as on a 60Hz one.
 *
 * ---------------------------------------------------------------------------------------------
 * Visibility contract — the part that must not be broken
 * ---------------------------------------------------------------------------------------------
 * --stcc-shift defaults to 0%, so with no script every card sits at rest. The tall scroll spacer,
 * the sticky pin and the mobile card stacking are ALL gated in CSS behind the [data-active]
 * attribute, which only this file ever sets, and only once the effect is actually wired up. If the
 * script fails to load or bails, the section renders as a plain grid of collection cards — never a
 * blank section, and never a long stretch of dead scroll.
 */

import { scroll } from 'vendor';
import { matchesMediaQuery } from 'theme';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const readNumber = (element, attribute, fallback) => {
  const value = parseFloat(element.getAttribute(attribute));
  return Number.isFinite(value) ? value : fallback;
};

// Close enough to the target that another frame would not move a card by a visible amount.
const SETTLE_EPSILON = 0.0005;

// A dropped frame (tab in the background, long task) must not let the glide jump the whole gap.
const MAX_FRAME_MS = 100;

/**
 * How far each card starts below its resting place, and over which slice of the pinned scroll it
 * travels back to 0. Everything that differs between desktop and mobile lives in here; the frame
 * loop below is identical for both.
 *
 *   start — starting offset, as a % of the card's own height
 *   from  — progress at which this card starts moving
 *   to    — progress at which it has landed
 */
const buildSegments = (host, count, isDesktop) => {
  // Desktop: a staircase. Card 1 starts one step down, card 2 two steps, card 3 three steps, and
  // they all share the same window — so the lower cards travel further in the same scroll distance,
  // move faster, and the whole row lands together.
  if (isDesktop) {
    const step = clamp(readNumber(host, 'offset-step', 30), 0, 100);

    // Optional sequencing on top: each card's window opens `delay` later than the one before it.
    // Capped so the window can never collapse — with the cap the last card still lands at exactly
    // progress 1 and every card keeps at least 20% of the scroll to travel in.
    const maxDelay = count > 1 ? 0.8 / (count - 1) : 0;
    const delay = Math.min(clamp(readNumber(host, 'stagger-delay', 0), 0, 40) / 100, maxDelay);
    const span = 1 - (count - 1) * delay;

    return Array.from({ length: count }, (_, index) => ({
      start: (index + 1) * step,
      from: index * delay,
      to: index * delay + span
    }));
  }

  // Mobile: only one full-height card fits on screen, so the cards are stacked in a single slot and
  // deal themselves out like a deck. Card 1 is already in place; each later card rises from below
  // and covers the one before it, one at a time.
  const slots = Math.max(count - 1, 1);

  return Array.from({ length: count }, (_, index) =>
    index === 0
      ? { start: 0, from: 0, to: 1 }
      : { start: 100, from: (index - 1) / slots, to: index / slots }
  );
};

class ScrolltriggerCollectionCards extends HTMLElement {
  #abortController;
  #resizeObserver = null;
  #stopScroll = null;
  #started = false;

  #pin = null;
  #cards = [];
  #segments = [];

  #pinHeight = 0;
  #pinTop = 0;

  // Where the scrollbar says we are, and where the cards have actually eased to.
  #targetProgress = 0;
  #smoothProgress = 0;

  // Time constant of the glide, in ms. 0 means "no smoothing" — paint the target directly.
  #tau = 0;

  // Extra pinned scroll AFTER the cards have landed, as a fraction of the animated scroll length.
  #holdFraction = 0;
  #rafId = null;
  #lastFrameTime = 0;

  // The card the merchant currently has selected in the theme editor. Held at rest so they can see
  // what they are editing instead of hunting for a card parked off the bottom of the pin.
  #selectedCard = null;

  connectedCallback() {
    this.#abortController = new AbortController();

    // The merchant can switch the effect off; the section is then a plain grid of cards.
    if (!this.hasAttribute('effect')) return;

    // Reduced motion: the section's CSS pins nothing in that case, so there is no state to undo —
    // leave the cards completely static.
    if (!matchesMediaQuery('motion-safe')) return;

    this.#pin = this.querySelector('.stcc__pin');
    this.#cards = Array.from(this.querySelectorAll('[data-stcc-card]'));

    if (!this.#pin || this.#cards.length === 0) return;

    const { signal } = this.#abortController;

    // Desktop and mobile use different choreographies, so rebuild when that breakpoint is crossed
    // rather than only at connect time.
    window
      .matchMedia(window.themeVariables.mediaQueries['sm'])
      .addEventListener('change', () => this.#build(), { signal });

    // Both heights are viewport-derived, so they change on rotation, window resize and mobile
    // chrome changes. Observing the host and the pin catches all of that without polling. (They no
    // longer react to the header — see the section's CSS for why everything is held constant.)
    this.#resizeObserver = new ResizeObserver(() => {
      this.#measure();
      this.#syncTarget();
    });
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(this.#pin);

    if (window.Shopify?.designMode) {
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          this.#selectedCard = event.target.closest('[data-stcc-card]');
          this.#paint();
        },
        { signal }
      );

      this.addEventListener(
        'shopify:block:deselect',
        () => {
          this.#selectedCard = null;
          this.#paint();
        },
        { signal }
      );
    }

    this.#start();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#teardown();
  }

  #start() {
    if (this.#started) return;

    this.#build();

    // Only now does the CSS switch the section into its pinned, full-height form. Setting this last
    // means a failure anywhere above leaves the plain, readable fallback in place.
    this.setAttribute('data-active', '');

    // Re-measure once the pin is actually sticky and sized, then hand over to the scroll loop.
    // scroll() with a bare callback fires on every scroll frame, rAF-throttled by Motion One.
    this.#measure();
    this.#snap();
    this.#stopScroll = scroll(() => this.#syncTarget());

    this.#started = true;
  }

  #teardown() {
    this.#stopScroll?.();
    this.#stopScroll = null;
    this.#stopTicking();
    this.#started = false;
    this.removeAttribute('data-active');

    for (const card of this.#cards) {
      card.style.removeProperty('--stcc-shift');
    }
  }

  #build() {
    this.#segments = buildSegments(this, this.#cards.length, matchesMediaQuery('sm'));
    this.#tau = clamp(readNumber(this, 'smoothing', 35), 0, 100) * 4;

    // A fraction rather than a length, so one value works at both breakpoints even though their
    // scroll lengths differ — this is why nothing here has to branch on the breakpoint.
    this.#holdFraction = clamp(readNumber(this, 'hold', 0), 0, 1);
    this.#measure();

    // The layout just changed under us, so land on the new position rather than gliding to it.
    this.#snap();
  }

  #measure() {
    this.#pinHeight = this.#pin.getBoundingClientRect().height;

    // The resolved `top` of the sticky element — the offset it parks at. Currently always 0 (the pin
    // deliberately ignores the header), but read from the CSS rather than hard-coded so the maths
    // stays correct if that offset is ever reintroduced.
    const pinTop = parseFloat(getComputedStyle(this.#pin).top);
    this.#pinTop = Number.isFinite(pinTop) ? pinTop : 0;
  }

  #readProgress() {
    const rect = this.getBoundingClientRect();

    // The host stays in flow and is the scroll spacer: it is one pin tall plus the scroll length the
    // merchant asked for, plus the hold. That whole surplus is how far the page scrolls while the
    // pin is stuck — but the cards must finish before the hold begins.
    //
    // The CSS lays out (scroll + scroll × hold), so dividing by (1 + hold) recovers the animated
    // scroll length on its own. Progress therefore reaches 1 at the end of that length and the
    // clamp holds it there for the rest of the pin, leaving the finished row still on screen
    // instead of starting to scroll away on the very frame the last card lands.
    const travel = (rect.height - this.#pinHeight) / (1 + this.#holdFraction);

    return travel > 0 ? clamp((this.#pinTop - rect.top) / travel, 0, 1) : 1;
  }

  /** Scroll (or a resize) moved the target. Glide toward it, or jump if smoothing is off. */
  #syncTarget() {
    this.#targetProgress = this.#readProgress();

    if (this.#tau <= 0) {
      this.#smoothProgress = this.#targetProgress;
      this.#paint();
      return;
    }

    this.#startTicking();
  }

  /** Land on the current scroll position immediately, with no glide. */
  #snap() {
    this.#stopTicking();
    this.#targetProgress = this.#readProgress();
    this.#smoothProgress = this.#targetProgress;
    this.#paint();
  }

  #startTicking() {
    if (this.#rafId !== null) return;
    this.#lastFrameTime = 0;
    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #stopTicking() {
    if (this.#rafId === null) return;
    cancelAnimationFrame(this.#rafId);
    this.#rafId = null;
    this.#lastFrameTime = 0;
  }

  // Runs only while the cards still have ground to cover — it stops itself once they settle, so
  // there is no permanent rAF loop burning battery on a page nobody is scrolling.
  #tick = (now) => {
    this.#rafId = null;

    const elapsed = this.#lastFrameTime ? Math.min(now - this.#lastFrameTime, MAX_FRAME_MS) : 16.7;
    this.#lastFrameTime = now;

    // Frame-rate independent easing: over a fixed wall-clock time the glide covers the same ground
    // whether the display runs at 60Hz or 120Hz. A plain `+= gap * 0.1` would not.
    const alpha = 1 - Math.exp(-elapsed / this.#tau);
    this.#smoothProgress += (this.#targetProgress - this.#smoothProgress) * alpha;

    if (Math.abs(this.#targetProgress - this.#smoothProgress) < SETTLE_EPSILON) {
      this.#smoothProgress = this.#targetProgress;
      this.#paint();
      this.#lastFrameTime = 0;
      return;
    }

    this.#paint();
    this.#rafId = requestAnimationFrame(this.#tick);
  };

  #paint() {
    for (let index = 0; index < this.#cards.length; index++) {
      const card = this.#cards[index];
      const segment = this.#segments[index];

      if (!segment) continue;

      if (card === this.#selectedCard) {
        card.style.setProperty('--stcc-shift', '0%');
        continue;
      }

      const span = segment.to - segment.from;
      const travelled = span > 0 ? clamp((this.#smoothProgress - segment.from) / span, 0, 1) : 1;

      // Linear within the segment, deliberately: the glide above is what softens the motion. Easing
      // here as well would compound into a sluggish, rubbery feel.
      card.style.setProperty('--stcc-shift', `${(segment.start * (1 - travelled)).toFixed(3)}%`);
    }
  }
}

if (!window.customElements.get('scrolltrigger-collection-cards')) {
  window.customElements.define('scrolltrigger-collection-cards', ScrolltriggerCollectionCards);
}
